import { useState, useCallback, useMemo } from "react";
import type {
  UIHashKey,
  SelectionState,
} from "@/features/auth/types/hashKeys.types";

/**
 * Custom hook for managing hash keys selection state
 */
export const useHashKeysSelection = (hashKeys: UIHashKey[]) => {
  const [selectedKeys, setSelectedKeys] = useState(new Set<string>());

  // Memoize hash key IDs to prevent recreation on every render
  const hashKeyIds = useMemo(
    () => hashKeys.map((hashKey) => hashKey.id),
    [hashKeys],
  );

  const handleKeySelect = useCallback((keyId: string) => {
    setSelectedKeys((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(keyId)) {
        newSelected.delete(keyId);
      } else {
        newSelected.add(keyId);
      }
      return newSelected;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedKeys((prev) =>
      prev.size === hashKeys.length ? new Set() : new Set(hashKeyIds),
    );
  }, [hashKeyIds, hashKeys.length]);

  const handleClearSelection = useCallback(() => {
    setSelectedKeys(new Set());
  }, []);

  // Memoize checkbox states for header to prevent unnecessary recalculations
  const selectionState: SelectionState = useMemo(() => {
    const allSelected =
      selectedKeys.size === hashKeys.length && hashKeys.length > 0;
    const someSelected =
      selectedKeys.size > 0 && selectedKeys.size < hashKeys.length;

    return {
      selectedKeys,
      allSelected,
      someSelected,
    };
  }, [selectedKeys, hashKeys.length]);

  return {
    selectionState,
    handleKeySelect,
    handleSelectAll,
    handleClearSelection,
  };
};
